import React, { useEffect, useMemo, useState } from "react";
import { DoorOpen, X } from "lucide-react";
import { Button } from "../ui/Button";
import FormField from "../ui/FormField";
import { getSunbedRooms, type SunbedRoomApi } from "../../services/sunbeds";
import type { SunbedWaitingListItem } from "../../services/sunbedWaitingList";
import { cn } from "../../lib/utils";

interface StartWaitingListModalProps {
  isOpen: boolean;
  item: SunbedWaitingListItem | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (roomId: number, tmaxId?: number) => void;
}

function isRoomAvailable(room: SunbedRoomApi): boolean {
  return String(room.availability).toLowerCase() === "available";
}

function formatRoomAvailability(status: string): string {
  const normalized = status.toLowerCase().replace(/_/g, "-");
  if (normalized === "available") return "Available";
  if (normalized === "in-session" || normalized === "in_session") return "In session";
  if (normalized === "cooling") return "Cooling";
  if (normalized === "maintenance") return "Maintenance";
  if (normalized === "cleaning") return "Cleaning";
  return status;
}

function formatRoomOptionLabel(room: SunbedRoomApi): string {
  if (isRoomAvailable(room)) return room.label;
  return `${room.label} (${formatRoomAvailability(room.availability)})`;
}

export const StartWaitingListModal: React.FC<StartWaitingListModalProps> = ({
  isOpen,
  item,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [rooms, setRooms] = useState<SunbedRoomApi[]>([]);
  const [roomId, setRoomId] = useState("");
  const [fetchingRooms, setFetchingRooms] = useState(false);

  useEffect(() => {
    if (!isOpen || !item) {
      setRooms([]);
      setRoomId("");
      setFetchingRooms(false);
      return;
    }

    let cancelled = false;

    const loadRooms = async () => {
      setFetchingRooms(true);
      setRoomId("");
      try {
        const res = await getSunbedRooms();
        const list: SunbedRoomApi[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        if (cancelled) return;

        // Prefer rooms that belong to this prepaid package's sunbed catalog
        const packageSunbedId = item.sunbed_id != null ? Number(item.sunbed_id) : null;
        const scoped =
          packageSunbedId != null
            ? list.filter((r: SunbedRoomApi) => Number(r.sunbed_id) === packageSunbedId)
            : list;
        const roomsForPicker = scoped.length > 0 ? scoped : list;

        setRooms(roomsForPicker);
        if (item.preferred_room_id) {
          const preferred = roomsForPicker.find(
            (r: SunbedRoomApi) => r.id === item.preferred_room_id
          );
          if (preferred && isRoomAvailable(preferred)) {
            setRoomId(String(item.preferred_room_id));
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load rooms for start session", err);
          setRooms([]);
        }
      } finally {
        if (!cancelled) setFetchingRooms(false);
      }
    };

    void loadRooms();
    return () => {
      cancelled = true;
    };
  }, [isOpen, item]);

  const preferredRoom = useMemo(() => {
    if (!item?.preferred_room_id) return null;
    return rooms.find((r) => r.id === item.preferred_room_id) ?? null;
  }, [item?.preferred_room_id, rooms]);

  const selectedRoom = useMemo(() => {
    if (!roomId) return null;
    return rooms.find((r) => r.id.toString() === roomId) ?? null;
  }, [roomId, rooms]);

  const roomOptions = useMemo(
    () =>
      rooms.map((r) => ({
        value: String(r.id),
        label: formatRoomOptionLabel(r),
      })),
    [rooms]
  );

  const preferredLabel =
    item?.preferred_room_label ||
    preferredRoom?.label ||
    (item?.preferred_room_id ? `Room #${item.preferred_room_id}` : null);

  const preferredStatus = preferredRoom
    ? formatRoomAvailability(preferredRoom.availability)
    : null;

  const selectedIsAvailable = selectedRoom ? isRoomAvailable(selectedRoom) : false;

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#402B69]">Start session</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 min-h-[220px] relative">
          <div className="rounded-lg bg-[#F3E8FF] px-4 py-3 text-sm text-[#402B69]">
            <p className="font-semibold">{item.customer}</p>
            <p className="text-xs mt-1 opacity-80">
              {item.package_name} · {item.duration_minutes} mins
            </p>
          </div>

          {fetchingRooms ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent" />
              <p className="text-sm font-medium text-gray-600">Loading sunbed rooms...</p>
              {item.preferred_room_label && (
                <p className="text-xs text-gray-500">
                  Pre-selecting {item.preferred_room_label}
                </p>
              )}
            </div>
          ) : (
            <>
              {preferredLabel && (
                <div className="rounded-lg border-2 border-[#885ABB] bg-[#FAF5FF] px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-[#F3E8FF] p-2 text-[#885ABB] shrink-0">
                      <DoorOpen className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#885ABB]">
                        Queued sunbed room
                      </p>
                      <p className="font-semibold text-gray-900 mt-0.5">{preferredLabel}</p>
                      {preferredStatus && (
                        <span
                          className={cn(
                            "inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full",
                            preferredStatus === "Available"
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-800"
                          )}
                        >
                          {preferredStatus}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <FormField
                label="Sunbed room *"
                name="start_room"
                type="select"
                value={roomId}
                onChange={(_, val) => setRoomId(val)}
                options={roomOptions}
                placeholder={
                  roomOptions.length > 0 ? "-- Select Room --" : "No rooms configured"
                }
              />

              {selectedRoom && !selectedIsAvailable && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This room is {formatRoomAvailability(selectedRoom.availability).toLowerCase()}.
                  Choose another room or wait until it is available.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-[#885ABB] hover:bg-[#7748A4] text-white min-w-[120px]"
            disabled={loading || fetchingRooms || !roomId || !selectedIsAvailable}
            onClick={() => onConfirm(parseInt(roomId, 10), selectedRoom?.tmax_id)}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Starting...
              </span>
            ) : fetchingRooms ? (
              "Loading..."
            ) : (
              "Start Session"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
